import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Paper,
  Collapse,
} from '@mui/material';
import { X, MagnifyingGlass, CaretDown, CaretUp } from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../../utils/api';
import LegalDocumentModal from '../../Legal/LegalDocumentModal';

// Match frontend/package.json version
const APP_VERSION = '0.1.0';

/** Match drawer sub-item to current route (supports optional activePathPrefixes e.g. create/edit). */
function menuChildMatchesLocation(child, pathname) {
  if (!child?.path) return false;
  if (pathname === child.path) return true;
  if (pathname.startsWith(`${child.path}/`)) return true;
  if (child.activePathPrefixes?.some((p) => pathname.startsWith(p))) return true;
  return false;
}

function MobileMenuDrawer({
  open,
  onClose,
  menuItems,
  isSupplier,
}) {
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [legalModal, setLegalModal] = useState({ open: false, fileName: null, title: '' });

  const LEGAL_DOCS = [
    { fileName: 'terms-conditions', title: 'Terms & Conditions' },
    { fileName: 'privacy-policy', title: 'Privacy Policy' },
    { fileName: 'cookies-policy', title: 'Cookies Policy' },
  ];

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue.trim().length >= 2) {
        performSearch(searchValue.trim());
      } else {
        setSearchResults([]);
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;

    const activeSections = {};
    menuItems.forEach((item) => {
      if (item.children?.some((child) => menuChildMatchesLocation(child, location.pathname))) {
        activeSections[item.title] = true;
      }
    });
    setExpandedSections(activeSections);
  }, [open, menuItems, location.pathname]);

  const performSearch = async (query) => {
    setIsSearching(true);
    try {
      const response = await api.get('/api/search', {
        params: { q: query, limit: 10 },
      });
      setSearchResults(response.data.results || []);
      setShowResults(true);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleResultClick = (result) => {
    setShowResults(false);
    setSearchValue('');
    onClose();

    if (isSupplier) {
      if (result.type === 'lead') {
        navigate(`/supplier/crm/leads/${result._id}`);
      }
      return;
    }

    // Couples: guests, suppliers, chats
    if (result.type === 'guest') {
      navigate(`/guest-list?guestId=${result._id}`);
    } else if (result.type === 'supplier') {
      navigate(`/dashboard/supplier/${result._id}`);
    } else if (result.type === 'chat') {
      navigate(`/chat/${result._id}`);
    }
  };

  const handleMenuItemClick = (path) => {
    if (!path) return;
    navigate(path);
    onClose();
  };

  const toggleSection = (title) => {
    setExpandedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  const formatVersion = (v) => {
    const parts = String(v).split('.');
    return `V${parts[0]}.${parts[1] || '0'}`;
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '100%',
          bgcolor: 'grey.100',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="h4"
            fontWeight={500}
            sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}
          >
            Menu
          </Typography>
          <IconButton onClick={onClose} aria-label="Close menu">
            <X size={24} weight="regular" />
          </IconButton>
        </Box>

        {/* Search */}
        <Box ref={searchRef} sx={{ px: 2, pt: 2, position: 'relative' }}>
          <TextField
            placeholder="Start your Search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            size="small"
            fullWidth
            sx={{
              bgcolor: 'background.paper',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.12)',
                },
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {isSearching ? (
                    <CircularProgress size={20} />
                  ) : (
                    <MagnifyingGlass size={20} weight="regular" />
                  )}
                </InputAdornment>
              ),
            }}
          />
          {showResults && searchResults.length > 0 && (
            <Paper
              sx={{
                position: 'absolute',
                top: '100%',
                left: 16,
                right: 16,
                mt: 1,
                maxHeight: 300,
                overflow: 'auto',
                zIndex: 1300,
                boxShadow: 3,
              }}
            >
              <List dense>
                {searchResults.map((r) => (
                  <ListItem
                    key={`${r.type}-${r._id}`}
                    button
                    onClick={() => handleResultClick(r)}
                  >
                    <ListItemText primary={r.title} secondary={r.subtitle} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
          {showResults && searchValue.trim().length >= 2 && !isSearching && searchResults.length === 0 && (
            <Paper sx={{ position: 'absolute', top: '100%', left: 16, right: 16, mt: 1, p: 2, zIndex: 1300, boxShadow: 3 }}>
              <Typography variant="body2" color="text.secondary">
                No results found
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Menu items */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
          <List disablePadding>
            {menuItems.map((item) => {
              if (item.children && item.children.length > 0) {
                const isExpanded = Boolean(expandedSections[item.title]);
                return (
                  <React.Fragment key={item.title}>
                    <ListItem
                      button
                      onClick={() => toggleSection(item.title)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                      {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                    </ListItem>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List disablePadding>
                        {item.children.map((child) => {
                          const childActive = menuChildMatchesLocation(child, location.pathname);
                          return (
                          <ListItem
                            key={child.title}
                            button
                            onClick={() => handleMenuItemClick(child.path)}
                            sx={{
                              pl: 3,
                              borderRadius: 1,
                              mb: 0.5,
                              bgcolor: childActive ? 'action.selected' : 'transparent',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <ListItemText
                              primary={child.title}
                              primaryTypographyProps={{ fontWeight: childActive ? 600 : 400 }}
                            />
                          </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </React.Fragment>
                );
              }

              return (
                <ListItem
                  key={item.title}
                  button
                  onClick={() => handleMenuItemClick(item.path)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.5,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {LEGAL_DOCS.map((doc) => (
            <Typography
              key={doc.fileName}
              component="button"
              onClick={() => {
                setLegalModal({ open: true, fileName: doc.fileName, title: doc.title });
                onClose();
              }}
              sx={{
                display: 'block',
                fontSize: '0.875rem',
                mb: 0.5,
                color: 'text.secondary',
                textDecoration: 'underline',
                cursor: 'pointer',
                border: 'none',
                background: 'none',
                textAlign: 'left',
                fontFamily: 'inherit',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {doc.title}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {formatVersion(APP_VERSION)}
          </Typography>
        </Box>
      </Box>

    </Drawer>

      <LegalDocumentModal
        open={legalModal.open}
        onClose={() => setLegalModal({ open: false, fileName: null, title: '' })}
        fileName={legalModal.fileName}
        title={legalModal.title}
      />
    </>
  );
}

export default MobileMenuDrawer;
